import type React from "react";
import {useState} from "react";
import {Skeleton, Stack, Typography} from "@mui/material";
import {useOutletContext} from "react-router";
import {CustomCard} from "~/components/Card/Card";
import type {SupplierDetailOutletContext} from "~/pages/supplier-360/types/Supplier360Types";
import {
    useChainEntitlementQuery,
    useChainPendingCreditRequestsQuery,
    useChainUnlocksQuery,
} from "~/pages/supplier-360/tabs/SupplierValueChain/hooks/useChainCreditQueries";
import {useUnlockChainMutation} from "~/pages/supplier-360/tabs/SupplierValueChain/hooks/useChainCreditMutations";
import {ChainCreditsStrip} from "./components/ChainCreditsStrip";
import {ChainLockedPanel} from "./components/ChainLockedPanel";
import {ChainUnlockDialog} from "./components/ChainUnlockDialog";
import {ChainRequestCreditsDialog} from "./components/ChainRequestCreditsDialog";
import {ValueChainSection} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/ValueChainSection";

type OpenDialog = 'unlock' | 'request' | null;


export const SupplierValueChainTab: React.FC = () => {
    const {entity} = useOutletContext<SupplierDetailOutletContext>();
    const [dialog, setDialog] = useState<OpenDialog>(null);

    const entitlementQuery = useChainEntitlementQuery(entity.databag_id);
    const entitlement = entitlementQuery.data ?? undefined;
    const unlocksQuery = useChainUnlocksQuery(entitlement?.id);
    const pendingQuery = useChainPendingCreditRequestsQuery(entitlement?.id);
    const unlock = useUnlockChainMutation();
    const pendingRequests = pendingQuery.data ?? [];
    if (entitlementQuery.isLoading || (entitlement && unlocksQuery.isLoading)) {
        return <Skeleton variant="rectangular" height={320} sx={{borderRadius: 1}}/>;
    }

    if (entitlementQuery.isError || unlocksQuery.isError) {
        return (
            <CustomCard title="Value chain">
                <Typography variant="body2" color="text.secondary">
                    Value chain credits could not be loaded.
                </Typography>
            </CustomCard>
        );
    }

    // No entitlement row: the API can't create one, and unlock / request both need its id.
    if (!entitlement) {
        return (
            <CustomCard title="Value chain">
                <Typography variant="body2" color="text.secondary">
                    Value chain isn't enabled for this databag. Contact Mithra to add credits.
                </Typography>
            </CustomCard>
        );
    }

    const isUnlocked = unlocksQuery.data?.some(row => row.entity === entity.id) ?? false;

    return (
        <Stack>
            <ChainCreditsStrip entitlement={entitlement}/>

            {isUnlocked && <ValueChainSection entity={entity}/>}

            {!isUnlocked && (
                <ChainLockedPanel
                    entitlement={entitlement}
                    pendingRequests={pendingRequests}
                    supplierName={entity.canonical_name}
                    onUnlock={() => setDialog('unlock')}
                    onRequest={() => setDialog('request')}
                />
            )}

            {dialog === 'unlock' && (
                <ChainUnlockDialog
                    entitlement={entitlement}
                    supplierName={entity.canonical_name}
                    isPending={unlock.isPending}
                    onCancel={() => setDialog(null)}
                    onConfirm={() => unlock.mutate(
                        {entitlement, entityId: entity.id, supplierName: entity.canonical_name},
                        {onSettled: () => setDialog(null)},
                    )}
                />
            )}
            {dialog === 'request' && (
                <ChainRequestCreditsDialog entitlement={entitlement} onClose={() => setDialog(null)}/>
            )}
        </Stack>
    );
};
